import React, { useState, useEffect } from 'react';
import { joinPersonalLoanForAdmin } from '../../api/AdminApi'; // Assuming you have this API
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';

const AddPersonalLoan = () => {
  const [loanTitle, setLoanTitle] = useState('');
  const [loanAmount, setLoanAmount] = useState(100000);
  const [interestRate, setInterestRate] = useState(12);
  const [loanTenure, setLoanTenure] = useState(12);
  const [processingFee, setProcessingFee] = useState(2);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('Salem');
  const [loanData, setLoanData] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [loanType, setLoanType] = useState('personal');
  const [emiType, setEmiType] = useState('fixed');
  const { customerEmail } = useParams(); 

  // Function to calculate end date based on start date and tenure
  const calculateEndDate = (start, months) => {
    if (!start || !months || months <= 0) return '';
    
    const startDateObj = new Date(start);
    const endDateObj = new Date(startDateObj);
    endDateObj.setMonth(endDateObj.getMonth() + parseInt(months));
    
    // Format to YYYY-MM-DD for input field
    return endDateObj.toISOString().split('T')[0];
  };

  // Update end date when start date or tenure changes
  useEffect(() => {
    if (startDate && loanTenure > 0) {
      const calculatedEndDate = calculateEndDate(startDate, loanTenure);
      setEndDate(calculatedEndDate);
    }
  }, [startDate, loanTenure]);

  // Calculate EMI and loan schedule
  useEffect(() => {
    if (loanAmount <= 0 || interestRate <= 0 || loanTenure <= 0) {
      setLoanData([]);
      return;
    }

    const calculatedData = [];
    const monthlyInterestRate = interestRate / 100 / 12;
    const processingFeeAmount = (loanAmount * processingFee) / 100;
    const principalAmount = loanAmount - processingFeeAmount;

    // EMI calculation formula
    const emi = loanAmount * monthlyInterestRate * 
                Math.pow(1 + monthlyInterestRate, loanTenure) / 
                (Math.pow(1 + monthlyInterestRate, loanTenure) - 1);

    const monthlyEMI = Math.round(emi);
    let remainingPrincipal = loanAmount;
    let totalInterestPaid = 0;
    let totalPaid = 0;

    for (let i = 1; i <= loanTenure; i++) {
      const interestForMonth = Math.round(remainingPrincipal * monthlyInterestRate);
      const principalForMonth = monthlyEMI - interestForMonth;
      
      remainingPrincipal -= principalForMonth;
      totalInterestPaid += interestForMonth;
      totalPaid += monthlyEMI;

      calculatedData.push({
        month: i,
        emi: monthlyEMI,
        principal: Math.round(principalForMonth),
        interest: interestForMonth,
        remainingBalance: Math.max(0, Math.round(remainingPrincipal)),
        totalInterest: Math.round(totalInterestPaid),
        totalPaid: Math.round(totalPaid),
      });
    }

    // Add summary row
    calculatedData.push({
      month: 'Total',
      emi: Math.round(monthlyEMI * loanTenure),
      principal: loanAmount,
      interest: Math.round(totalInterestPaid),
      remainingBalance: 0,
      totalInterest: Math.round(totalInterestPaid),
      totalPaid: Math.round(monthlyEMI * loanTenure),
      isSummary: true
    });

    setLoanData(calculatedData);
  }, [loanAmount, loanTenure, interestRate, processingFee]);

  const createPersonalLoanPayload = () => {
    const monthlyInterestRate = interestRate / 100 / 12;
    const processingFeeAmount = (loanAmount * processingFee) / 100;
    const emi = loanAmount * monthlyInterestRate * 
                Math.pow(1 + monthlyInterestRate, loanTenure) / 
                (Math.pow(1 + monthlyInterestRate, loanTenure) - 1);

    const loanPayload = {
      loanTitle: loanTitle,
      loanAmount: parseFloat(loanAmount),
      interestRate: parseFloat(interestRate),
      loanTenure: parseInt(loanTenure),
      processingFee: parseFloat(processingFee),
      processingFeeAmount: processingFeeAmount,
      startDate: startDate,
      endDate: endDate,
      location: location,
      loanType: loanType,
      emiType: emiType,
      monthlyEMI: Math.round(emi),
      loanSchedule: loanData.filter(item => !item.isSummary).map(item => ({
        month: item.month,
        emi: item.emi,
        principal: item.principal,
        interest: item.interest,
        remainingBalance: item.remainingBalance,
        totalInterest: item.totalInterest,
        totalPaid: item.totalPaid,
      })),
      totalInterest: Math.round(loanData[loanData.length - 1]?.totalInterest || 0),
      totalAmount: Math.round(emi * loanTenure),
    };

    return loanPayload;
  };

  const resetForm = () => {
    setLoanTitle('');
    setLoanAmount(0);
    setInterestRate(0);
    setLoanTenure(0);
    setProcessingFee(0);
    setStartDate('');
    setEndDate('');
    setLocation('Salem');
    setLoanType('personal');
    setEmiType('fixed');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const loanPayload = createPersonalLoanPayload();

    joinPersonalLoanForAdmin(loanPayload, customerEmail)
      .then((res) => {
        toast.success(res.data.message);
        console.log("Final Loan Payload:", loanPayload);
        
        // Reset form values to 0 after successful submission
        resetForm();
        
        // Set success message
        setSuccessMessage('Personal loan has been successfully created!');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      })
      .catch((err) => {
        toast.error("Error creating loan:", err.message);
        alert("Failed to create loan. Please try again.");
      });
  };

  const handleBack = () => {
    window.history.back();
  };

  // Handle start date change
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  // Handle tenure change
  const handleTenureChange = (months) => {
    setLoanTenure(months);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={handleBack}
            className="group flex items-center space-x-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl border border-blue-200 hover:border-blue-300 transition-all duration-300 hover:scale-105"
          >
            <svg className="w-5 h-5 text-blue-600 group-hover:text-blue-700 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            <span className="font-semibold text-blue-700 group-hover:text-blue-800">Back</span>
          </button>
          
          <div className="text-center flex-1 max-w-2xl">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Personal Loan Planner
            </h1>
            <p className="text-lg text-gray-600 font-medium">
              Plan your financial journey with our comprehensive loan calculator
            </p>
          </div>
          
          <div className="w-32"></div> {/* Spacer for balance */}
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-blue-100">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 text-center">
              <div className="flex items-center justify-center space-x-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <p className="text-lg font-semibold">{successMessage}</p>
              </div>
            </div>
          )}

          <div className="p-8">
            {/* Form Section */}
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-10">
                {/* Input Fields */}
                {[
                  {
                    id: 'loanTitle',
                    label: 'Loan Title',
                    type: 'text',
                    value: loanTitle,
                    onChange: setLoanTitle,
                    placeholder: 'e.g., Home Renovation Loan'
                  },
                  {
                    id: 'loanAmount',
                    label: 'Loan Amount (₹)',
                    type: 'number',
                    value: loanAmount,
                    onChange: setLoanAmount
                  },
                  {
                    id: 'interestRate',
                    label: 'Interest Rate (%)',
                    type: 'number',
                    value: interestRate,
                    onChange: setInterestRate,
                    step: "0.1"
                  },
                  {
                    id: 'loanTenure',
                    label: 'Tenure (months)',
                    type: 'number',
                    value: loanTenure,
                    onChange: handleTenureChange
                  },
                  {
                    id: 'processingFee',
                    label: 'Processing Fee (%)',
                    type: 'number',
                    value: processingFee,
                    onChange: setProcessingFee,
                    step: "0.1"
                  },
                  {
                    id: 'startDate',
                    label: 'Start Date',
                    type: 'date',
                    value: startDate,
                    onChange: handleStartDateChange
                  },
                  {
                    id: 'endDate',
                    label: 'End Date',
                    type: 'date',
                    value: endDate,
                    onChange: setEndDate,
                    readOnly: true,
                    className: "bg-gray-100 cursor-not-allowed"
                  },
                  {
                    id: 'location',
                    label: 'Location',
                    type: 'text',
                    value: location,
                    onChange: setLocation,
                    placeholder: 'e.g., Salem, Chennai'
                  },
                  {
                    id: 'loanType',
                    label: 'Loan Type',
                    type: 'select',
                    value: loanType,
                    onChange: setLoanType,
                    options: [
                      { value: 'personal', label: 'Personal Loan' },
                      { value: 'business', label: 'Business Loan' },
                      { value: 'education', label: 'Education Loan' },
                      { value: 'medical', label: 'Medical Loan' }
                    ]
                  },
                  {
                    id: 'emiType',
                    label: 'EMI Type',
                    type: 'select',
                    value: emiType,
                    onChange: setEmiType,
                    options: [
                      { value: 'fixed', label: 'Fixed EMI' },
                      { value: 'reducing', label: 'Reducing Balance' }
                    ]
                  }
                ].map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label htmlFor={field.id} className="block text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {field.label}
                    </label>
                    <div className="relative">
                      {field.type === 'select' ? (
                        <select
                          id={field.id}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="w-full px-4 py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-blue-50/50"
                        >
                          {field.options.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={field.id}
                          type={field.type}
                          value={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          className={`w-full px-4 py-3 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-blue-50/50 placeholder-gray-400 ${
                            field.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''
                          }`}
                          placeholder={field.placeholder}
                          readOnly={field.readOnly}
                          step={field.step}
                        />
                      )}
                      <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-blue-300 transition-all duration-200 pointer-events-none"></div>
                    </div>
                    {field.id === 'endDate' && endDate && (
                      <p className="text-xs text-green-600 font-medium mt-1">
                        ✅ Automatically calculated from start date and tenure
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <div className="flex justify-center">
                <button
                  type="submit"
                  className="group relative px-12 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                >
                  <span className="relative z-10 flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                    <span>Create Personal Loan</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </form>

            {/* Summary Cards */}
            {loanData.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12 mb-8">
                <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-6 rounded-2xl border border-blue-200">
                  <div className="text-blue-600 font-bold text-lg mb-2">Loan Amount</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{loanAmount.toLocaleString()}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-indigo-100 to-blue-100 p-6 rounded-2xl border border-indigo-200">
                  <div className="text-blue-600 font-bold text-lg mb-2">Monthly EMI</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{loanData[0]?.emi?.toLocaleString() || '0'}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-6 rounded-2xl border border-blue-200">
                  <div className="text-blue-600 font-bold text-lg mb-2">Total Interest</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{(loanData[loanData.length - 1]?.totalInterest || 0).toLocaleString()}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-indigo-100 to-blue-100 p-6 rounded-2xl border border-indigo-200">
                  <div className="text-blue-600 font-bold text-lg mb-2">Total Payment</div>
                  <div className="text-2xl font-bold text-gray-900">
                    ₹{(loanData[loanData.length - 1]?.totalPaid || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            {/* Table Section */}
            {loanData.length > 0 && (
              <div className="mt-12">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">Loan Repayment Schedule</h3>
                  <div className="flex items-center space-x-2 text-blue-600">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path>
                    </svg>
                    <span className="font-semibold">{loanTenure} Months Plan</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-2xl overflow-hidden shadow-lg">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                          <th className="px-6 py-4 text-left font-bold uppercase tracking-wider text-sm">Month</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">EMI (₹)</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Principal (₹)</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Interest (₹)</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Balance (₹)</th>
                          <th className="px-6 py-4 text-right font-bold uppercase tracking-wider text-sm">Total Interest (₹)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-blue-100/20">
                        {loanData.map((row, index) => (
                          <tr 
                            key={index} 
                            className={`transition-colors duration-200 hover:bg-blue-50/10 ${
                              row.isSummary 
                                ? 'bg-indigo-100/20 font-bold text-black' 
                                : 'text-black'
                            }`}
                          >
                            <td className={`px-6 py-4 whitespace-nowrap ${row.isSummary ? 'text-lg' : ''}`}>
                              {row.month}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.emi ? `₹${row.emi.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.principal ? `₹${row.principal.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.interest ? `₹${row.interest.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.remainingBalance !== undefined ? `₹${row.remainingBalance.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-6 py-4 text-right whitespace-nowrap">
                              {row.totalInterest ? `₹${row.totalInterest.toLocaleString()}` : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <p>💫 Plan your financial future with confidence</p>
        </div>
      </div>
    </div>
  );
};

export default AddPersonalLoan;